/**
 * WO-B parts 5–6 (G-139, OD-198) — how the office shows stock whose cost
 * nobody knows. One statement of the words and the rules; every office page
 * that shows a cost, a margin, a floor or a value built on them calls these.
 *
 * - A cost nobody knows reads "cost unknown" — never €0.
 * - A figure that cannot be worked out (a margin over an unknown cost) reads "—".
 * - A total that leaves stock of unknown cost out says so with a count — of
 *   sales, lots, entries or items, or of units of ONE item in its own unit
 *   (never stems, centimetres and pieces added up as "units").
 * - A floor that cannot be worked out reads "cannot check" — the kiosk's word
 *   (part 7); no office screen shows a line's floor, and the office's
 *   below-floor list only ever holds lines that have one.
 *
 * The server stores the KNOWN part of a cost and counts the rest (sales:
 * `unknownCostLineCount`, lines: `unknownCostQty`, bouquets:
 * `unknownCostComponentCount`, stock and waste: `unknownCostQty`). A null
 * money field means "nobody knows" — and `Number(null)` is 0, so nothing here
 * ever calls it.
 */

export const COST_UNKNOWN = "cost unknown";
export const NO_FIGURE = "—";

/** How a page prints a euro amount (each page keeps its own style). */
export type MoneyFormat = (eur: number) => string;

/**
 * A money or quantity field as a number, or null when it holds none. A null,
 * an empty string or a non-number is null — never 0.
 */
export function knownAmount(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** True when a count or a quantity of unknown-cost stock is above zero. */
export function hasUnknown(countOrQty: string | number | null | undefined): boolean {
  return (knownAmount(countOrQty) ?? 0) > 0;
}

/** A cost that is known or not at all (a lot's, a slice's): "€1.20" / "cost unknown". */
export function costOrUnknown(
  value: string | number | null | undefined,
  money: MoneyFormat,
): string {
  const n = knownAmount(value);
  return n == null ? COST_UNKNOWN : money(n);
}

/**
 * A cost of which a part may be unknown — a sale's, a line's, a bouquet's: the
 * stored figure is the known part. "€4.20", "€4.20 + cost unknown", or just
 * "cost unknown" when nothing of it is known.
 */
export function partlyKnownCost(
  knownPart: string | number | null | undefined,
  someUnknown: boolean,
  money: MoneyFormat,
): string {
  const n = knownAmount(knownPart);
  if (!someUnknown) return n == null ? NO_FIGURE : money(n);
  return n == null || n === 0 ? COST_UNKNOWN : `${money(n)} + ${COST_UNKNOWN}`;
}

/**
 * A bouquet's cost (WO-B part 3): the known part of its stems and paper, and
 * "+ cost unknown" when any component's cost is unknown.
 */
export function bouquetCostText(
  build: { totalCogsEur?: string | null; unknownCostComponentCount?: number | null },
  money: MoneyFormat,
): string {
  return partlyKnownCost(build.totalCogsEur, hasUnknown(build.unknownCostComponentCount), money);
}

/**
 * A cost after its label ending in "cost": "cost €4.20", "cost €4.20 + cost
 * unknown", and "cost unknown" — never "cost cost unknown".
 */
export function afterCostLabel(label: string, costText: string): string {
  return costText === COST_UNKNOWN ? `${label} unknown` : `${label} ${costText}`;
}

/**
 * A figure built on a cost (a margin, a profit): "—" unless the cost under it
 * is wholly known — a margin over a part-known cost would count the rest as free.
 */
export function figureIfCostKnown(
  value: string | number | null | undefined,
  costKnown: boolean,
  money: MoneyFormat,
): string {
  const n = knownAmount(value);
  return !costKnown || n == null ? NO_FIGURE : money(n);
}

/**
 * Add up costs of which some may be unknown (a bouquet flower's lot slices):
 * the known sum, and how many had no cost.
 */
export function sumKnownCosts(values: ReadonlyArray<string | number | null | undefined>): {
  knownEur: number;
  unknownCount: number;
} {
  let knownEur = 0;
  let unknownCount = 0;
  for (const value of values) {
    const n = knownAmount(value);
    if (n == null) unknownCount += 1;
    else knownEur += n;
  }
  return { knownEur, unknownCount };
}

/** A quantity for a sentence: 12, 2.5 — three decimals at most, never "12.000". */
function qtyWord(qty: number): string {
  return String(Math.round(qty * 1000) / 1000);
}

/**
 * The one shape of every count note: "<n> <one|many><tail>", or null when
 * there is nothing to count.
 */
function countNote(
  value: string | number | null | undefined,
  one: string,
  many: string,
  tail: string,
): string | null {
  const n = knownAmount(value) ?? 0;
  if (n <= 0) return null;
  const word = n === 1 ? one : many;
  return `${qtyWord(n)}${word ? ` ${word}` : ""}${tail}`;
}

/** "1 sale includes stock of unknown cost" / "3 sales include …"; null for none. */
export function salesWithUnknownCostNote(count: number | null | undefined): string | null {
  return countNote(count, "sale includes", "sales include", " stock of unknown cost");
}

/** "2 items include stock of unknown cost" (a value over several items); null for none. */
export function itemsWithUnknownCostNote(count: number | null | undefined): string | null {
  return countNote(count, "item includes", "items include", " stock of unknown cost");
}

/**
 * ONE item's units left out of its price or value, in the item's own unit:
 * "3 STEM of unknown cost not counted". Never for a sum over several items —
 * their units do not add up.
 */
export function unknownQtyNotCountedNote(
  qty: string | number | null | undefined,
  unit: string,
): string | null {
  return countNote(qty, unit, unit, " of unknown cost not counted");
}

/** "2 lots of unknown cost not counted" (stock on shelves); null for none. */
export function unknownLotsNotCountedNote(count: number | null | undefined): string | null {
  return countNote(count, "lot", "lots", " of unknown cost not counted");
}

/** "1 entry of unknown cost not counted" (waste); null for none. */
export function unknownEntriesNotCountedNote(count: number | null | undefined): string | null {
  return countNote(count, "entry", "entries", " of unknown cost not counted");
}

/**
 * ONE item's usable units with no supplier price: "12 with no price not
 * counted" (in its own unit when known: "12 STEM …") — the supplier reports
 * read priced lots only, so a borrowed (estimated) cost is left out as well
 * as an unknown one.
 */
export function unpricedQtyNotCountedNote(
  qty: string | number | null | undefined,
  unit = "",
): string | null {
  return countNote(qty, unit, unit, " with no price not counted");
}

/**
 * G-175 (KI-153): "1 receive with no price" / "3 receives with no price" —
 * receives whose cost was estimated or unknown, which a spend total adds as
 * €0 (they booked no money); null for none.
 */
export function unpricedReceivesNote(count: number | null | undefined): string | null {
  return countNote(count, "receive", "receives", " with no price");
}

/** "2 components of unknown cost" (a bouquet); null for none. */
export function unknownComponentsNote(count: number | null | undefined): string | null {
  return countNote(count, "component", "components", " of unknown cost");
}

/** Notes that apply, joined for one hint line: "a · b"; null when none does. */
export function joinNotes(...notes: Array<string | null | undefined>): string | null {
  const present = notes.filter((n): n is string => Boolean(n));
  return present.length > 0 ? present.join(" · ") : null;
}
